import { EventEmitter } from 'events';
import { UTC } from 'rewire-common';
import { Modal, IModalState, Form, IFormContext, validator, error } from 'rewire-ui';
import { capitalize } from '../utility/StringUtils';
import { INameEntity, UdfDocumentType, _udfKeyMap } from '../DataTypes';
import { archiveUnArchive } from '../graphql/Mutations';
import { confirmationModel } from '../ui/ConfirmationDialog';
import { notifications } from '../ui/Notifications';

export interface IArchiveDeleteDialogModelState extends IModalState {
  nameEntity?: INameEntity;
  type: UdfDocumentType;
  task: 'archive' | 'delete';
}

export default abstract class ArchiveDeleteDialogModel extends Modal<IArchiveDeleteDialogModelState> {
  public form: Form;
  private __onEvent: EventEmitter;

  constructor(type: UdfDocumentType) {
    super('');

    this.state.type = type;
    this.form = Form.create((form: IFormContext) => ({
      endDate: form.date().label('As Of Date').placeholder('enter As Of Date').validators('required').autoFocus(),
    }));
    this.action('cancel').action('save', this.submit, { variant: 'contained', type: 'submit' });

    this.__onEvent = new EventEmitter();
  }

  reset() {
    this.form.reset();
    this.state.nameEntity = undefined;
  }

  load(nameEntity: INameEntity, task: 'archive' | 'delete') {
    this.reset();
    this.state.nameEntity = nameEntity;
    this.state.task = task;
    this.state.title = `${this.taskDisplay} ${this.name}`;
    this.open();
  }

  get nameEntity() {
    return this.state.nameEntity;
  }

  get name() {
    return this.nameEntity?.name ?? '';
  }

  get task() {
    return this.state.task;
  }

  get taskDisplay() {
    return capitalize(this.task);
  }

  get type() {
    return this.state.type;
  }

  get typeDisplayLowercase() {
    return _udfKeyMap[this.type];
  }

  get typeDisplay() {
    return capitalize(this.typeDisplayLowercase);
  }

  get endDate() {
    return this.form.field.endDate.value;
  }

  get endDateString() {
    return this.endDate.toDateString();
  }

  get onEvent() {
    return this.__onEvent;
  }

  abstract _delete(id: string, endDate?: UTC): Promise<boolean>;

  abstract _getConfirmationContentComponent(): () => JSX.Element;

  async delete(): Promise<boolean> {
    const result = await this._delete(this.nameEntity!.id, this.endDate);
    if (result) {
      notifications.success(`Successfully deleted ${this.typeDisplayLowercase}`);
      return true;
    }
    notifications.error(`${this.typeDisplay} deletion failed`);
    return false;
  }

  private save = async (): Promise<boolean> => {
    if (!this.nameEntity) return false;
    let result: boolean = false;
    if (this.task === 'archive') {
      result = await archiveUnArchive('archive', this.type, this.nameEntity, this.endDate);
    } else if (this.task === 'delete') {
      result = await this.delete();
    }
    return result;
  };

  private confirmArchiveDelete = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      const confirmationForm = Form.create(
        (form: IFormContext) => ({
          name: form
            .string()
            .label(`${this.typeDisplay} Name`)
            .placeholder(`enter ${this.typeDisplayLowercase} name`)
            .validators('required', validator('==', this.name, error('name does not match.')))
            .autoFocus(),
        }),
        {},
        { validateOnUpdate: true, updateOnChange: true },
      );
      confirmationForm.validateForm(); // Prime the form with validation errors.

      confirmationModel.load(`${this.taskDisplay} ${this.name} as of ${this.endDateString}?`, {
        yesAction: async () => {
          if (!confirmationForm.hasChanges || confirmationForm.hasErrors) {
            throw new Error('confirmation validation failed');
          }
          const result = await this.save();
          resolve(true);
          if (result) {
            this.onEvent.emit(this.task);
          }
          return true;
        },
        noAction: () => {
          resolve(false);
          return true;
        },
        yesDisabled: () => !confirmationForm.hasChanges || confirmationForm.hasErrors,
        title: `Confirm ${this.taskDisplay} ${this.name}`,
        maxWidth: 'sm',
        secondaryMessage: this._getConfirmationContentComponent(),
        form: confirmationForm,
      });
    });
  };

  submit = async () => {
    if (!this.nameEntity) return false;
    return await this.form.submitAsync(this.confirmArchiveDelete);
  };
}
